import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';
import { useVersionCheck } from '@/hooks/use-version-check';
import { useFeatureFlag } from '@/contexts/FeatureFlagContext';
import { useTranslation } from 'react-i18next';
import { APP_VERSION } from '@/lib/version';
import { Capacitor } from '@capacitor/core';
import { AlertTriangle } from 'lucide-react';

const TESTFLIGHT_URL = 'https://testflight.apple.com/join/hEdyUQsm';

type UpdateTarget = {
  url: string | null;
  label: string;
};

function getUpdateTarget(platform: string, iosStoreUrl: string | null, androidStoreUrl: string | null): UpdateTarget {
  if (platform === 'ios') {
    // If ios_store_url is a TestFlight link
    if (iosStoreUrl?.includes('testflight.apple.com')) {
      return { url: iosStoreUrl, label: 'TestFlight' };
    }
    // If ios_store_url is an App Store link
    if (iosStoreUrl) {
      return { url: iosStoreUrl, label: 'App Store' };
    }
    // Fallback to hardcoded TestFlight
    return { url: TESTFLIGHT_URL, label: 'TestFlight' };
  }

  if (platform === 'android') {
    if (androidStoreUrl) {
      return { url: androidStoreUrl, label: 'Google Play' };
    }
    return { url: null, label: 'Google Play' };
  }

  return { url: null, label: '' };
}

const ForceUpdateDialog = () => {
  const { needsUpdate, latestVersion, releaseNotes, iosStoreUrl, androidStoreUrl } = useVersionCheck();
  const { isEnabled } = useFeatureFlag();
  const { t } = useTranslation();

  // Safety: don't show during app review — reviewers install latest version
  if (!Capacitor.isNativePlatform() || !needsUpdate || !isEnabled('force_update')) return null;

  // Extra safety: if review mode is on, never block the app
  if (isEnabled('app_review_mode', false)) return null;

  const platform = Capacitor.getPlatform();
  const target = getUpdateTarget(platform, iosStoreUrl, androidStoreUrl);
  const isUrlMissing = !target.url;

  const handleUpdate = async () => {
    if (!target.url) return;
    try {
      const { Browser } = await import('@capacitor/browser');
      await Browser.open({ url: target.url });
    } catch {
      window.open(target.url, '_blank');
    }
  };

  return (
    <AlertDialog open>
      <AlertDialogContent className="rounded-2xl max-w-[340px]" onEscapeKeyDown={(e) => e.preventDefault()}>
        <AlertDialogHeader>
          <AlertDialogTitle className="text-center text-lg">{t('forceUpdate.title')}</AlertDialogTitle>
          <AlertDialogDescription className="text-center space-y-2">
            <p>
              {t('forceUpdate.currentVersion')} <span className="font-mono font-medium text-foreground">v{APP_VERSION}</span> {t('forceUpdate.noLongerSupported')}，
              {t('forceUpdate.pleaseUpdate')} <span className="font-mono font-medium text-foreground">v{latestVersion}</span> {t('forceUpdate.toContinue')}。
            </p>
            {releaseNotes && (
              <p className="text-xs text-muted-foreground pt-1">{releaseNotes}</p>
            )}
            {isUrlMissing && (
              <div className="flex items-center gap-1.5 justify-center text-xs text-destructive pt-1">
                <AlertTriangle className="h-3.5 w-3.5" />
                <span>{target.label} 更新链接未配置，请联系管理员</span>
              </div>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="sm:justify-center">
          <AlertDialogAction onClick={handleUpdate} disabled={isUrlMissing} className="w-full">
            {isUrlMissing
              ? `${target.label} 链接未配置`
              : `${t('forceUpdate.updateNow')}（${target.label}）`
            }
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default ForceUpdateDialog;
